import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * Read-only identifier with a copy button. Used for the stable Template ID /
 * Package ID (shared by every version) and the per-version record ID.
 */
const StableIdField = ({ label, value, placeholder = '—', hint, testId, compact = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" data-testid={testId}>
        <span>{label}:</span>
        <code className="font-mono text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded">{value || placeholder}</code>
        {value && (
          <button
            type="button"
            onClick={handleCopy}
            className="p-0.5 text-gray-400 hover:text-indigo-600 transition-colors"
            title={`Copy ${label}`}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </button>
        )}
      </span>
    );
  }

  return (
    <div data-testid={testId}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          readOnly
          value={value || ''}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 font-mono text-sm text-gray-800 cursor-default focus:outline-none"
        />
        <button
          type="button"
          onClick={handleCopy}
          disabled={!value}
          className="p-2 text-gray-500 border border-gray-200 rounded-lg hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title={`Copy ${label}`}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
};

export default StableIdField;
